import {
  Box,
  Button,
  Flex,
  FormControl,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  Skeleton,
  Stack,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import NotepadImg from '../../../../assets/images/NotepadImg.svg';
import { CustomDrawer, PrimaryButton } from '../../../../components/custom';
import { AddIcon, QueueListWithBgIcon } from '../../../../components/icons';
import useFetchBillBeneficiaries from '../../../../hooks/useGetBillBeneficiariesFromFirestore';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getBillersCategoriesAsync } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import { IBillBeneficiary } from '../../../../types/beneficiary';
import AddBeneficiaryForm from './AddBeneficiaryForm';
import BeneficiaryCard from './BeneficiaryCard';

const SKELETON_COUNT = 6;
const TABS = ['airtime', 'data', 'electricity', 'tv', 'betting'] as const;
const TAB_CATEGORY_MAP: Record<(typeof TABS)[number], string> = {
  airtime: 'airtime',
  data: 'data',
  electricity: 'electricity',
  tv: 'television',
  betting: 'betting',
};

export default function ManageBeneficiaries() {
  const dispatch = useAppDispatch();

  const {
    isOpen: isSaveOpen,
    onOpen: onSaveOpen,
    onClose: onSaveClose,
  } = useDisclosure();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] =
    useState<(typeof TABS)[number]>('airtime');

  const handleSearchBeneficiaries = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchQuery(e.target.value);
  };

  const { status: imageStatus, value: billCategoriesData } = useAppSelector(
    (state) => state.billerCategories
  );

  const [beneficiariesList, setBeneficiariesList] = useState<
    IBillBeneficiary[]
  >([]);

  const { status, getBeneficiaries } = useFetchBillBeneficiaries();

  const fetchBeneficiaries = useCallback(async () => {
    const beneficiaries = await getBeneficiaries();
    setBeneficiariesList(beneficiaries as IBillBeneficiary[]);
  }, [getBeneficiaries]);

  useEffect(() => {
    fetchBeneficiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { status: saveStatus } = useAppSelector(
    (state) => state.saveBillBeneficiary
  );
  const lastSaveStatusRef = useRef(saveStatus);
  useEffect(() => {
    if (saveStatus === 'success' && lastSaveStatusRef.current !== 'success') {
      fetchBeneficiaries();
    }
    lastSaveStatusRef.current = saveStatus;
  }, [saveStatus, fetchBeneficiaries]);

  const isLoading =
    status === 'loading' || status === 'idle' || imageStatus === 'loading';
  const hasFailed = status === 'failed';

  const queryTerms = searchQuery
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  const matchesQuery = (bill: IBillBeneficiary): boolean => {
    if (!queryTerms.length) return true;
    const haystack = [bill?.billAlias, bill?.billerName, bill?.customerId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return queryTerms.every((term) => haystack.includes(term));
  };

  const getBeneficiaryList = (billCategory: string): IBillBeneficiary[] => {
    if (!beneficiariesList?.length) return [];
    return beneficiariesList.filter(
      (bill) =>
        bill?.billCategory?.toLowerCase() === billCategory && matchesQuery(bill)
    );
  };

  const skeletonItems: IBillBeneficiary[] = new Array(SKELETON_COUNT).fill({});

  const renderBeneficiaryCardGrid = (filteredList: IBillBeneficiary[]) => {
    if (isLoading) {
      return (
        <Grid
          gap={6}
          px={8}
          templateColumns={{
            base: 'repeat(1, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)',
          }}
          alignItems="stretch"
        >
          {skeletonItems.map((_, index) => (
            <GridItem
              w="100%"
              h="full"
              // eslint-disable-next-line react/no-array-index-key
              key={`bill-skeleton-${index}`}
            >
              <Skeleton
                isLoaded={false}
                fadeDuration={1}
                startColor={index % 2 === 0 ? 'ash.300' : 'ash.400'}
                endColor={index % 2 === 0 ? 'ash.400' : 'ash.300'}
                h="full"
                minH="180px"
                sx={{ borderRadius: '16px' }}
              />
            </GridItem>
          ))}
        </Grid>
      );
    }

    if (filteredList?.length === 0) {
      return (
        <Box py="80px" maxW="307px" mx="auto">
          <Image src={NotepadImg} alt="" mb={2} mx="auto" />
          <Text textAlign="center" mb={1} fontWeight={500}>
            {queryTerms.length
              ? 'No beneficiaries match your search.'
              : 'There is nothing to see here yet'}
          </Text>
        </Box>
      );
    }

    return (
      <Grid
        gap={6}
        px={8}
        templateColumns={{
          base: 'repeat(1, 1fr)',
          lg: 'repeat(3, 1fr)',
          xl: 'repeat(4, 1fr)',
        }}
        alignItems="stretch"
      >
        {filteredList.map((beneficiary, index) => {
          const key =
            beneficiary?.id ??
            beneficiary?.customerId ??
            `bill-bene-${index}`;
          return (
            <GridItem w="100%" h="full" key={key}>
              <BeneficiaryCard
                beneficiary={beneficiary}
                status={status}
                selectedTab={selectedTab}
                billCategoriesData={billCategoriesData}
              />
            </GridItem>
          );
        })}
      </Grid>
    );
  };

  useEffect(() => {
    dispatch(getBillersCategoriesAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent={{ base: 'start', md: 'space-between' }}
        alignItems={{ base: 'start', md: 'center' }}
        gap="6"
        mb="42px"
      >
        <Text>
          {status === 'success'
            ? `All Beneficiaries - ${beneficiariesList.length}`
            : 'All Beneficiaries'}
        </Text>

        <Flex
          justifyContent={{ base: 'space-between', md: 'initial' }}
          alignItems="center"
          gap={{ base: 4, md: '44px' }}
        >
          <FormControl id="search-bill-beneficiaries">
            <InputGroup>
              <Input
                variant="rounded"
                placeholder="Search beneficiaries"
                value={searchQuery}
                type="search"
                onChange={handleSearchBeneficiaries}
                sx={{
                  backgroundColor: 'ash.100',
                  borderRadius: '24px',
                  minW: { base: '100%', md: '355px' },
                  height: '44px',
                }}
              />
            </InputGroup>
          </FormControl>

          <Box>
            <PrimaryButton
              leftIcon={<AddIcon mr={2} />}
              minW="165px"
              h="44px"
              sx={{
                borderRadius: 24,
              }}
              onClick={onSaveOpen}
            >
              Add Beneficiary
            </PrimaryButton>
            <CustomDrawer
              isOpen={isSaveOpen}
              onClose={onSaveClose}
              drawerHeader="Add Beneficiary"
            >
              <AddBeneficiaryForm />
            </CustomDrawer>
          </Box>
        </Flex>
      </Box>

      <section
        style={{
          marginTop: '32px',
        }}
      >
        <Box
          background="#fff"
          border="1px solid #e0e3e6"
          borderRadius="24px"
          padding={6}
          minH="100%"
          height="100%"
        >
          <HStack justifyContent="space-between" alignItems="center" mb="28px">
            <HStack spacing={{ base: '8px', md: '18px' }}>
              <QueueListWithBgIcon boxSize="32px" />

              <Heading
                as="h2"
                fontWeight={700}
                fontSize="1.125rem"
                lineHeight="133%"
                noOfLines={1}
              >
                Beneficiary List
              </Heading>
            </HStack>
          </HStack>

          {hasFailed ? (
            <Stack align="center" spacing={3} py={10}>
              <Text color="gray.300" textAlign="center">
                We couldn&apos;t load your beneficiaries. Please try again.
              </Text>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchBeneficiaries}
              >
                Try again
              </Button>
            </Stack>
          ) : (
            <Tabs
              onChange={(index) => {
                setSelectedTab(TABS[index] ?? 'airtime');
              }}
            >
              <TabList>
                {TABS.map((tab) => (
                  <Tab
                    _selected={{ color: 'black' }}
                    sx={{
                      textTransform: 'capitalize',
                    }}
                    key={tab}
                  >
                    {tab}
                  </Tab>
                ))}
              </TabList>
              <TabIndicator
                mt="-1.5px"
                height="2px"
                bg="black"
                borderRadius="1px"
              />
              <TabPanels>
                {TABS.map((tab) => (
                  <TabPanel key={tab}>
                    {selectedTab === tab &&
                      renderBeneficiaryCardGrid(
                        getBeneficiaryList(TAB_CATEGORY_MAP[tab])
                      )}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          )}
        </Box>
      </section>
    </>
  );
}
